import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, endOfMonth, startOfMonth, format } from "date-fns";
import { ptBR } from "date-fns/locale";

export interface MonthlyData {
  month: string;
  monthLabel: string;
  income: number;
  expense: number;
  balance: number;
}

export interface CategoryData {
  id: string;
  name: string;
  color: string;
  icon: string;
  amount: number;
  percentage: number;
  transactionCount: number;
}

export interface MonthlyComparison {
  currentMonthExpenses: number;
  previousMonthExpenses: number;
  percentageChange: number;
  daysRemaining: number;
  projectedTotal: number;
  dailyAverage: number;
  currentMonthIncome: number;
  previousMonthIncome: number;
  incomePercentageChange: number;
  incomeProjectedTotal: number;
  incomeDailyAverage: number;
  // Smart projection breakdown
  fixedExpensesTotal: number;
  variableExpensesProjected: number;
  fixedIncomeTotal: number;
  variableIncomeProjected: number;
}

export interface DashboardAnalytics {
  monthlyData: MonthlyData[];
  categoryData: CategoryData[];
  monthlyComparison: MonthlyComparison;
  totalExpenses12Months: number;
  totalIncome12Months: number;
  averageMonthlyExpense: number;
  isLoading: boolean;
  error: Error | null;
}

export function useDashboardAnalytics(): DashboardAnalytics {
  const today = new Date();

  const { data: rpcResult, isLoading, error } = useQuery({
    queryKey: ["dashboard-analytics"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase.rpc("get_dashboard_analytics", {
        p_months_back: 12,
      });

      if (error) throw error;
      return data as any;
    },
  });

  // Map RPC result to expected interfaces
  const rawMonthly = rpcResult?.monthlyData || [];
  const monthlyData: MonthlyData[] = rawMonthly.map((m: any) => {
    const monthDate = new Date(m.month + "-15");
    const label = format(monthDate, "MMM", { locale: ptBR });
    return {
      month: m.month,
      monthLabel: label.charAt(0).toUpperCase() + label.slice(1),
      income: Number(m.income),
      expense: Number(m.expense),
      balance: Number(m.balance),
    };
  });

  const rawCategories = rpcResult?.categoryData || [];
  const totalCurrentMonthExpenses = rawCategories.reduce(
    (sum: number, c: any) => sum + Number(c.amount), 0
  );
  const categoryData: CategoryData[] = rawCategories.map((c: any) => ({
    id: c.id,
    name: c.name,
    color: c.color,
    icon: c.icon,
    amount: Number(c.amount),
    percentage: totalCurrentMonthExpenses > 0
      ? (Number(c.amount) / totalCurrentMonthExpenses) * 100
      : 0,
    transactionCount: Number(c.transactionCount),
  }));

  const comp = rpcResult?.comparison || {};
  const currentExpenses = Number(comp.currentMonthExpenses || 0);
  const previousExpenses = Number(comp.previousMonthExpenses || 0);
  const currentIncome = Number(comp.currentMonthIncome || 0);
  const previousIncome = Number(comp.previousMonthIncome || 0);
  const daysPassed = Number(comp.daysPassed || 1);
  const daysInMonth = Number(comp.daysInMonth || 30);
  const daysRemaining = daysInMonth - daysPassed;

  // Smart projection: fixed costs are known, only variable costs are extrapolated
  const fixedExpensesPaid = Number(comp.fixedExpensesPaid || 0);
  const fixedExpensesPending = Number(comp.fixedExpensesPending || 0);
  const variableExpensesPaid = Number(comp.variableExpensesPaid || 0);
  const fixedIncomePaid = Number(comp.fixedIncomePaid || 0);
  const fixedIncomePending = Number(comp.fixedIncomePending || 0);
  const variableIncomePaid = Number(comp.variableIncomePaid || 0);

  // Fixed total = already paid fixed + known pending fixed (full month is known)
  const fixedExpensesTotal = fixedExpensesPaid + fixedExpensesPending;
  const fixedIncomeTotal = fixedIncomePaid + fixedIncomePending;

  // Variable projection = extrapolate paid variable expenses across full month
  const variableDailyAverage = daysPassed > 0 ? variableExpensesPaid / daysPassed : 0;
  const variableExpensesProjected = variableDailyAverage * daysInMonth;

  const variableIncomeDailyAvg = daysPassed > 0 ? variableIncomePaid / daysPassed : 0;
  const variableIncomeProjected = variableIncomeDailyAvg * daysInMonth;

  // Smart projection = known fixed + extrapolated variable
  const projectedTotal = fixedExpensesTotal + variableExpensesProjected;
  const incomeProjectedTotal = fixedIncomeTotal + variableIncomeProjected;

  // Daily averages (total, for display)
  const dailyAverage = daysPassed > 0 ? currentExpenses / daysPassed : 0;
  const incomeDailyAverage = daysPassed > 0 ? currentIncome / daysPassed : 0;

  const percentageChange = previousExpenses > 0
    ? ((currentExpenses - previousExpenses) / previousExpenses) * 100
    : 0;
  const incomePercentageChange = previousIncome > 0
    ? ((currentIncome - previousIncome) / previousIncome) * 100
    : 0;

  const monthlyComparison: MonthlyComparison = {
    currentMonthExpenses: currentExpenses,
    previousMonthExpenses: previousExpenses,
    percentageChange,
    daysRemaining,
    projectedTotal,
    dailyAverage,
    currentMonthIncome: currentIncome,
    previousMonthIncome: previousIncome,
    incomePercentageChange,
    incomeProjectedTotal,
    incomeDailyAverage,
    fixedExpensesTotal,
    variableExpensesProjected,
    fixedIncomeTotal,
    variableIncomeProjected,
  };

  const totals = rpcResult?.totals || {};
  const totalExpenses12Months = Number(totals.totalExpenses12Months || 0);
  const totalIncome12Months = Number(totals.totalIncome12Months || 0);
  const averageMonthlyExpense = totalExpenses12Months / 12;

  return {
    monthlyData,
    categoryData,
    monthlyComparison,
    totalExpenses12Months,
    totalIncome12Months,
    averageMonthlyExpense,
    isLoading,
    error: error as Error | null,
  };
}
